import React, { useEffect, useState } from 'react'
import Toggle from 'react-toggle'
import Button from 'src/global/Button'
import { useDispatch, useSelector } from 'react-redux'
import WaiterLoginModal from './WaiterLoginModal'

import { Wrapper } from './styles'
import { usersSelector } from 'src/state/selectors'
import { UserDevice, UserPatchType } from '@meniudigital/shared'
import { patchCurrentUser, removeDevice } from 'src/state/users'
import Checkbox from 'src/global/Checkbox'
import { Screen } from '../../screens'
import { Link } from 'react-router-dom'
import { userEndpoints } from 'src/api'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBell, faChevronDown, faQrcode } from '@fortawesome/free-solid-svg-icons'
import { IS_DESKTOP_OR_TABLET } from '../HomeScreen'
import SetupPanel from './SetupPanel'
import MdToggle from '../HomeScreen/MdToggle'

type OrderingMode = 'table' | 'online'

const getAppUrl = `${process.env.REACT_APP_MENU_BASE_URL}/get-app`

export default function ConsumerOrders() {
  const usersState = useSelector(usersSelector)
  const user = usersState.user!
  const dispatch = useDispatch()

  const { ordering } = user
  const { isCallWaiterEnabled, isTableOrderingEnabled, isOnlineOrderingEnabled } = ordering || {}

  const hasEnoughDevices = (user.devices || []).filter(x => x.isPushNotificationsEnabled).length >= 1

  const [checklistValues, setChecklistValues] = useState([false, false, false, false])
  const [onlineChecklistValues, setOnlineChecklistValues] = useState([false, false, false, false])
  const [isShowingInfos, setIsShowingInfos] = useState(true)
  const [orderingMode, setOrderingMode] = useState<OrderingMode>('table')
  const [hoveredImage, setHoveredImage] = useState<string | null>(null)

  useEffect(() => {
    userEndpoints.registerUserAction({ actionType: 'ViewConsumerOrders', data: {} })
  }, [])

  useEffect(() => {
    const isTableLive = Boolean(hasEnoughDevices && (isCallWaiterEnabled || isTableOrderingEnabled))
    const isOnlineLive = Boolean(hasEnoughDevices && isOnlineOrderingEnabled)
    setChecklistValues([false, false, false, false].fill(isTableLive))
    setOnlineChecklistValues([false, false, false, false].fill(isOnlineLive))
    setIsShowingInfos(!isTableLive && !isOnlineLive)
  }, [user])

  if (!user) {
    return null
  }

  const isEverythingChecked = checklistValues.every(Boolean)
  const isOnlineEverythingChecked = onlineChecklistValues.every(Boolean)

  const isTableDisabled = !isEverythingChecked || !hasEnoughDevices
  const isOnlineDisabled = !isOnlineEverythingChecked || !hasEnoughDevices || !user.ordering?.deliveryZonesConfig?.zones?.length

  const confirmRemoveDevice = async (device: UserDevice) => {
    await window.showModal({
      title: `Șterge dispozitiv/ospătar`,
      text: `Ești sigur că dorești să ștergi dispozitivul ${device.deviceType} aparținând ospătarului ${device.waiterName}?`,
    })
    dispatch(removeDevice(device.deviceId))
  }

  const toggleCallWaiter = async () => {
    if (!ordering?.isCallWaiterEnabled) {
      await window.showModal({
        title: `Activare funcționalitate`,
        text: `Ești sigur că dorești să activezi funcționalitatea "Cheamă ospătarul"?`,
        confirmButtonText: 'Da, activează',
        cancelButtonText: 'Nu',
      })
      dispatch(patchCurrentUser({ type: UserPatchType.OrderingEnableCallWaiter, data: true }))
    } else {
      await window.showModal({
        title: `Dezactivare funcționalitate`,
        text: `Ești sigur că dorești dezactivarea funcționalității "Cheamă ospătarul"?`,
        confirmButtonText: 'Da, dezactivează',
        cancelButtonText: 'Nu',
      })
      dispatch(patchCurrentUser({ type: UserPatchType.OrderingEnableCallWaiter, data: false }))
    }
  }

  const toggleTableOrdering = async () => {
    if (!ordering?.isTableOrderingEnabled) {
      await window.showModal({
        title: `Activare funcționalitate`,
        text: `Ești sigur că dorești să activezi funcționalitatea de comandă la masă?`,
        confirmButtonText: 'Da, activează',
        cancelButtonText: 'Nu',
      })
      dispatch(patchCurrentUser({ type: UserPatchType.OrderingEnableTableOrdering, data: true }))
    } else {
      await window.showModal({
        title: `Dezactivare funcționalitate`,
        text: `Ești sigur că dorești să dezactivezi funcționalitatea de comandă la masă?`,
        confirmButtonText: 'Da, dezactivează',
        cancelButtonText: 'Nu',
      })
      dispatch(patchCurrentUser({ type: UserPatchType.OrderingEnableTableOrdering, data: false }))
    }
  }

  const toggleOnlineOrdering = async () => {
    if (!ordering?.isOnlineOrderingEnabled) {
      await window.showModal({
        title: `Activare Comenzi Online`,
        text: `Funcționalitatea "Comenzi Online" permite clienților să plaseze comenzi de pe orice dispozitiv, fără a fi prezenți în local.\n\nAceștia vor putea alege între ridicare personală (pickup) și livrare.\n\nEști sigur că dorești să activezi comenzile online?`,
        confirmButtonText: 'Da, activează',
        cancelButtonText: 'Nu',
      })
      dispatch(patchCurrentUser({ type: UserPatchType.OrderingEnableOnlineOrdering, data: true }))
    } else {
      await window.showModal({
        title: `Dezactivare Comenzi Online`,
        text: `Ești sigur că dorești să dezactivezi funcționalitatea de comenzi online?\n\nClienții nu vor mai putea plasa comenzi de la distanță.`,
        confirmButtonText: 'Da, dezactivează',
        cancelButtonText: 'Nu',
      })
      dispatch(patchCurrentUser({ type: UserPatchType.OrderingEnableOnlineOrdering, data: false }))
    }
  }

  const tableCheckList = [
    'Doresc să folosesc funcționalitatea',
    'Am cod QR cu număr integrat pe fiecare masă',
    <span>
      <Link to={`${Screen.SetLocation}?returnTo=${Screen.ConsumerOrders}`}>Locația indicată pe hartă</Link> este corectă
    </span>,
    <span>
      Ospătarii au{' '}
      <a target="_blank" href={getAppUrl}>
        aplicația
      </a>{' '}
      instalată
    </span>,
  ]

  const onlineCheckList = [
    'Doresc să folosesc funcționalitatea',
    <span>
      Ospătarii au{' '}
      <a target="_blank" href={getAppUrl}>
        aplicația
      </a>{' '}
      instalată
    </span>,
    <span>
      <Link to={`${Screen.SetLocation}?returnTo=${Screen.ConsumerOrders}`}>Locația restaurantului</Link> este corectă
    </span>,
    <span>
      <Link to={Screen.SetDeliveryZones}>Zonele de livrare</Link> sunt configurate
    </span>,
  ]

  const toggleCheckbox = (index: number) => {
    const newCheckboxValues = [...checklistValues]
    const newValue = !newCheckboxValues[index]
    newCheckboxValues[index] = newValue

    if (!newValue) {
      newCheckboxValues.slice(index).forEach((_, i) => (newCheckboxValues[i + index] = false))
    }
    setChecklistValues(newCheckboxValues)
  }

  const toggleOnlineCheckbox = (index: number) => {
    const newCheckboxValues = [...onlineChecklistValues]
    const newValue = !newCheckboxValues[index]
    newCheckboxValues[index] = newValue

    if (!newValue) {
      newCheckboxValues.slice(index).forEach((_, i) => (newCheckboxValues[i + index] = false))
    }
    setOnlineChecklistValues(newCheckboxValues)
  }

  const isTableLive = Boolean(hasEnoughDevices && (isCallWaiterEnabled || isTableOrderingEnabled))
  const isOnlineLive = Boolean(hasEnoughDevices && isOnlineOrderingEnabled)

  const toggleInfos = () => {
    setIsShowingInfos(!isShowingInfos)
  }

  return (
    <Wrapper>
      {IS_DESKTOP_OR_TABLET && <SetupPanel />}
      <div className="page">
        <div className="banner">
          <span>
            {orderingMode === 'table' ? 'Comenzi la Masă și "Cheamă Ospătarul"' : 'Comenzi Online cu Livrare sau Ridicare'}
          </span>
          <div className="mode-toggle-container">
            <MdToggle<OrderingMode>
              value={orderingMode}
              options={[
                { value: 'table', label: 'La Masă' },
                { value: 'online', label: 'Cu Livrare' },
              ]}
              onChange={value => setOrderingMode(value)}
            />
          </div>
        </div>
        <div className={`settings ${hasEnoughDevices ? '' : 'disabled'}`}>
          {orderingMode === 'table' && (
            <>
              <div className={`module-overview ${isShowingInfos ? '' : 'collapsed'}`}>
                <div className="header" onClick={toggleInfos}>
                  <h2 className="title">
                    <FontAwesomeIcon className="chevron" icon={faChevronDown} />
                    Cum funcționează?
                  </h2>
                </div>
                <div className="collapsable">
                  <div className="collapsable-content">
                    <div className="images" onMouseLeave={() => setHoveredImage(null)}>
                      {[
                        'ordering-explainers/order-in-browser.jpeg',
                        'ordering-explainers/order-summary.jpeg',
                        'ordering-explainers/watch.jpeg',
                        'ordering-explainers/push-notifications.jpeg',
                        'ordering-explainers/tablet-new-order.jpg',
                      ].map(src => (
                        <span key={src} className="image-wrapper" onMouseEnter={() => setHoveredImage(src)}>
                          <img src={src} alt="" />
                        </span>
                      ))}
                      {hoveredImage && (
                        <div className="image-preview">
                          <img src={hoveredImage} alt="" />
                        </div>
                      )}
                    </div>
                    <ul className="info-blocks">
                      <li className="info-block">
                        După vizualizarea meniului, clienții vor putea chema un ospătar / plasa o comandă{' '}
                        <strong>direct din browser</strong>.
                      </li>
                      <li className="info-block">
                        Numărul mesei la care se află va fi luat automat din codul QR, astfel că pentru comanda la masă trebuie să
                        generezi coduri <strong>QR cu numărul mesei integrat</strong>, din secțiunea
                        <span className="qr-button" onClick={() => window.openMyQrCodeModal()}>
                          Codul meu QR <FontAwesomeIcon className="icon" icon={faQrcode} />
                        </span>
                      </li>
                      <li className="info-block">
                        În momentul în care un client cheamă un ospătar sau plasează o comandă, ospătarii tăi sunt notificați pe
                        telefon, tabletă și smartwatch prin intermediul <a href={getAppUrl}>aplicației MeniuDigital</a>.
                      </li>
                      <li className="info-block">
                        Pentru ca funcționalitatea să fie activă pentru clienți, trebuie să ai cel puțin{' '}
                        <strong>un dispozitiv</strong> de ospătar autentificat (și cu notificările active).
                      </li>
                    </ul>
                    <div style={{ marginTop: 24 }}>
                      {tableCheckList.map((checklistItem, index) => (
                        <Checkbox
                          key={index}
                          text={checklistItem}
                          isChecked={checklistValues[index]}
                          onChange={() => toggleCheckbox(index)}
                          disabled={(index > 0 && !checklistValues.slice(0, index).every(Boolean)) || isTableLive}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {checklistValues[checklistValues.length - 1] && !hasEnoughDevices && (
                  <span className="error">
                    Trebuie să ai cel puțin un dispozitiv de ospătar cu notificările active pentru a putea activa
                    funcționalitățile.
                  </span>
                )}
              </div>

              <div className={`shadow-container ${isTableDisabled ? 'disabled' : ''}`}>
                <div className={`setting grouped  ${isTableDisabled ? 'disabled' : ''}`}>
                  <div className="setting-name">
                    <div className="setting-icon">
                      <img src="/icons/bell.png" alt="Call Waiter icon" />
                    </div>
                    <span>Modul "Cheamă Ospătarul"</span>
                    {ordering?.isCallWaiterEnabled && !isTableDisabled && <div className="bullet" />}
                  </div>
                  <div className="right-side">
                    <div className="react-toggle-wrapper">
                      <span className="only-desktop">
                        {isTableDisabled ? 'Indisponibil' : ordering?.isCallWaiterEnabled ? 'Activ' : 'Inactiv'}
                      </span>
                      <Toggle
                        className="toggle"
                        checked={!isTableDisabled && ordering?.isCallWaiterEnabled}
                        icons={false}
                        onChange={toggleCallWaiter}
                      />
                    </div>
                  </div>
                </div>

                <div className={`setting grouped ${isTableDisabled ? 'disabled' : ''}`}>
                  <div className="setting-name">
                    <div className="setting-icon">
                      <img src="/icons/bell-order.png" alt="Order icon" />
                    </div>
                    <span>Modul "Comandă la Masă"</span>
                    {ordering?.isTableOrderingEnabled && !isTableDisabled && <div className="bullet" />}
                  </div>
                  <div className="right-side">
                    <div className="react-toggle-wrapper">
                      <span className="only-desktop">
                        {isTableDisabled ? 'Indisponibil' : ordering?.isTableOrderingEnabled ? 'Activ' : 'Inactiv'}
                      </span>
                      <Toggle
                        className="toggle"
                        checked={!isTableDisabled && ordering?.isTableOrderingEnabled}
                        icons={false}
                        onChange={toggleTableOrdering}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {orderingMode === 'online' && (
            <>
              <div className={`module-overview ${isShowingInfos ? '' : 'collapsed'}`}>
                <div className="header" onClick={toggleInfos}>
                  <h2 className="title">
                    <FontAwesomeIcon className="chevron" icon={faChevronDown} />
                    Cum funcționează?
                  </h2>
                </div>
                <div className="collapsable">
                  <div className="collapsable-content">
                    <div className="images" onMouseLeave={() => setHoveredImage(null)}>
                      {[
                        'ordering-explainers/order-in-browser.jpeg',
                        'ordering-explainers/order-summary.jpeg',
                        'ordering-explainers/push-notifications.jpeg',
                        'ordering-explainers/tablet-new-order.jpg',
                      ].map(src => (
                        <span key={src} className="image-wrapper" onMouseEnter={() => setHoveredImage(src)}>
                          <img src={src} alt="" />
                        </span>
                      ))}
                      {hoveredImage && (
                        <div className="image-preview">
                          <img src={hoveredImage} alt="" />
                        </div>
                      )}
                    </div>
                    <ul className="info-blocks">
                      <li className="info-block">
                        Clienții pot plasa comenzi online direct de pe <strong>meniudigital.ro/{user.username}</strong>, de pe
                        orice dispozitiv, fără a fi prezenți în local.
                      </li>
                      <li className="info-block">
                        Costul livrării și comanda minimă sunt calculate automat în funcție de{' '}
                        <Link to={Screen.SetDeliveryZones}>zonele de livrare</Link> configurate de tine.
                      </li>
                      <li className="info-block">
                        În momentul în care un client plasează o comandă, stafful tău e notificat pe tabletă sau smartphone prin
                        intermediul <a href={getAppUrl}>aplicației MeniuDigital</a>.
                      </li>
                      <li className="info-block">
                        Pentru ca funcționalitatea să fie activă, trebuie să ai cel puțin un dispozitiv de ospătar autentificat,
                        cu notificările active.
                      </li>
                    </ul>
                    <div style={{ marginTop: 24 }}>
                      {onlineCheckList.map((checklistItem, index) => (
                        <Checkbox
                          key={index}
                          text={checklistItem}
                          isChecked={onlineChecklistValues[index]}
                          onChange={() => toggleOnlineCheckbox(index)}
                          disabled={(index > 0 && !onlineChecklistValues.slice(0, index).every(Boolean)) || isOnlineLive}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {onlineChecklistValues[onlineChecklistValues.length - 1] && !hasEnoughDevices && (
                  <span className="error">
                    Trebuie să ai cel puțin un dispozitiv de ospătar cu notificările active pentru a putea activa
                    funcționalitățile.
                  </span>
                )}
              </div>

              <div className={`shadow-container ${isOnlineDisabled ? 'disabled' : ''}`}>
                <div className={`setting grouped ${isOnlineDisabled ? 'disabled' : ''}`}>
                  <div className="setting-name">
                    <div className="setting-icon">
                      <img src="/icons/bell-order.png" alt="Online ordering icon" />
                    </div>
                    <span>Comenzi Online cu Livrare/Pickup</span>
                    {ordering?.isOnlineOrderingEnabled && !isOnlineDisabled && <div className="bullet" />}
                  </div>
                  <div className="right-side">
                    <div className="react-toggle-wrapper">
                      <span className="only-desktop">
                        {isOnlineDisabled ? 'Indisponibil' : ordering?.isOnlineOrderingEnabled ? 'Activ' : 'Inactiv'}
                      </span>
                      <Toggle
                        className="toggle"
                        checked={!isOnlineDisabled && ordering?.isOnlineOrderingEnabled}
                        icons={false}
                        onChange={toggleOnlineOrdering}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="panel info" style={{ marginTop: '16px', padding: '40px 40px', textAlign: 'center' }}>
          <div className="background-waves">
            <img src="/misc/waves.png" />
          </div>

          {user?.devices.length ? (
            <>
              <span>Dispozitive/Ospătari autentificați</span>
              {user.devices.map(x => (
                <div key={x.deviceId} className="device">
                  <div
                    title={x.isPushNotificationsEnabled ? 'Notificări active' : 'Notificări oprite'}
                    className={`notifications-badge ${x.isPushNotificationsEnabled ? 'green' : 'red'}`}
                  >
                    <FontAwesomeIcon icon={faBell} />
                  </div>
                  <div className="device-info">
                    <span className="waiter-name">
                      {x.waiterName}{' '}
                      {!x.isPushNotificationsEnabled ? <span className="notifications-off"> (notificări oprite)</span> : ''}
                    </span>
                    <div className="device-type">
                      <img
                        src={`icons/${
                          x.deviceType.toLowerCase().includes('iphone') || x.deviceType.toLowerCase().includes('apple')
                            ? 'apple'
                            : 'android'
                        }.png`}
                      />
                      {x.deviceType}
                    </div>
                  </div>
                  <Button variant="as-text-danger" text="Șterge" onClick={() => confirmRemoveDevice(x)} />
                </div>
              ))}
            </>
          ) : (
            <>
              <p style={{ fontSize: 20, fontWeight: 500 }}>Nu ai niciun dispozitiv adăugat.</p>
              <p style={{ margin: '8px 0' }}>Poți adăuga primul dispozitiv de ospătar făcând click aici:</p>
              <Button text="Adaugă ospătar" icon={faQrcode} onClick={() => window.openWaiterLoginModal?.()} />
            </>
          )}
        </div>
        {!IS_DESKTOP_OR_TABLET && <SetupPanel />}
      </div>
      <WaiterLoginModal />
    </Wrapper>
  )
}
